// /var/www/speakiq/frontend/src/pages/ResetPassword.js
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../utils/api';

/* ── Portal detection (same logic as Login.js) ─────────────────────────── */
const hostname = window.location.hostname;
const isTenantSubdomain = (
  hostname.endsWith('.speakiq.uk') &&
  !['admin', 'tutors', 'www'].some(s => hostname === s + '.speakiq.uk') &&
  hostname !== 'speakiq.uk'
);
const PORTAL =
  hostname === 'admin.speakiq.uk'  ? 'superadmin' :
  hostname === 'tutors.speakiq.uk' ? 'tutor'      :
  isTenantSubdomain                ? 'student'    :
  'student';

const THEMES = {
  student:    { accent: '#00B4D8', btnBg: '#00B4D8', btnHover: '#0087A8', panelBg: 'linear-gradient(135deg,#00B4D8,#0087A8)' },
  superadmin: { accent: '#DC2626', btnBg: '#DC2626', btnHover: '#b91c1c', panelBg: 'linear-gradient(135deg,#DC2626,#b91c1c)' },
  tutor:      { accent: '#10B981', btnBg: '#10B981', btnHover: '#059669', panelBg: 'linear-gradient(135deg,#10B981,#059669)' },
};
const T = THEMES[PORTAL] || THEMES.student;

export default function ResetPassword() {
  const [token, setToken]       = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm]   = useState('');
  const [showPw, setShowPw]     = useState(false);
  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState('');
  const [done, setDone]         = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const t = params.get('token') || '';
    setToken(t);
    if (!t) setError('No reset token found. Please request a new password reset link.');
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!password || password.length < 8) {
      return setError('Password must be at least 8 characters.');
    }
    if (password !== confirm) {
      return setError('Passwords do not match.');
    }
    setLoading(true);
    try {
      await api.post('/api/auth/reset-password', { token, password });
      setDone(true);
    } catch (err) {
      setError(err?.response?.data?.error || 'Something went wrong. Please try again or request a new link.');
    } finally {
      setLoading(false);
    }
  };

  const inputStyle = {
    width: '100%', padding: '12px 16px',
    border: '2px solid #E5E7EB', borderRadius: 10,
    fontSize: 15, outline: 'none', boxSizing: 'border-box',
    color: '#1F2937', background: '#fff',
  };

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg,#060D14 0%,#0D1B2A 100%)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20,
    }}>
      <div style={{
        background: '#fff', borderRadius: 16, width: '100%', maxWidth: 440,
        padding: '40px 36px', boxShadow: '0 30px 80px rgba(0,0,0,0.4)',
      }}>
        {/* Logo */}
        <div style={{ textAlign: 'center', marginBottom: 28 }}>
          <div style={{ fontWeight: 900, fontSize: 22, color: '#0D1B2A' }}>
            Speak<span style={{ color: T.accent }}>IQ</span>
          </div>
        </div>

        {done ? (
          /* ── Success state ── */
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: 48, marginBottom: 12 }}>✅</div>
            <h2 style={{ fontSize: 20, fontWeight: 800, color: '#111827', margin: '0 0 10px' }}>
              Password updated!
            </h2>
            <p style={{ color: '#6B7280', fontSize: 14, lineHeight: 1.6, margin: '0 0 28px' }}>
              Your password has been changed successfully. You can now log in with your new password.
            </p>
            <button
              onClick={() => navigate('/login')}
              style={{
                width: '100%', padding: '14px', background: T.btnBg, color: '#fff',
                border: 'none', borderRadius: 10, fontSize: 15, fontWeight: 700, cursor: 'pointer',
              }}>
              Go to Login →
            </button>
          </div>
        ) : (
          /* ── Reset form ── */
          <>
            <h2 style={{ fontSize: 22, fontWeight: 800, color: '#111827', margin: '0 0 6px' }}>
              Set new password
            </h2>
            <p style={{ color: '#6B7280', fontSize: 14, margin: '0 0 24px', lineHeight: 1.5 }}>
              Choose a strong password with at least 8 characters.
            </p>

            {error && (
              <div style={{
                background: '#FEE2E2', color: '#EF4444', padding: '10px 14px',
                borderRadius: 8, fontSize: 13, marginBottom: 16,
              }}>
                {error}
              </div>
            )}

            {!token ? null : (
              <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                <div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                    <label style={{ fontSize: 13, fontWeight: 600, color: '#374151' }}>New Password</label>
                    <button type="button" onClick={() => setShowPw(v => !v)}
                      style={{ background: 'none', border: 'none', color: '#6B7280', fontSize: 12, cursor: 'pointer', padding: 0 }}>
                      {showPw ? 'Hide' : 'Show'}
                    </button>
                  </div>
                  <input
                    type={showPw ? 'text' : 'password'}
                    value={password}
                    onChange={e => setPassword(e.target.value)}
                    placeholder="At least 8 characters"
                    style={inputStyle}
                    autoFocus
                  />
                </div>

                <div>
                  <label style={{ fontSize: 13, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 6 }}>
                    Confirm Password
                  </label>
                  <input
                    type={showPw ? 'text' : 'password'}
                    value={confirm}
                    onChange={e => setConfirm(e.target.value)}
                    placeholder="Repeat your new password"
                    style={{
                      ...inputStyle,
                      borderColor: confirm && confirm !== password ? '#EF4444' : '#E5E7EB',
                    }}
                  />
                  {confirm && confirm !== password && (
                    <p style={{ color: '#EF4444', fontSize: 12, margin: '4px 0 0' }}>Passwords do not match</p>
                  )}
                </div>

                {/* Password strength indicator */}
                {password.length > 0 && (
                  <div style={{ display: 'flex', gap: 4, marginTop: -8 }}>
                    {[1,2,3,4].map(i => {
                      const strength = password.length >= 12 ? 4 : password.length >= 10 ? 3 : password.length >= 8 ? 2 : 1;
                      const colors = ['#EF4444','#F59E0B','#10B981','#10B981'];
                      return (
                        <div key={i} style={{
                          flex: 1, height: 3, borderRadius: 2,
                          background: i <= strength ? colors[strength - 1] : '#E5E7EB',
                          transition: 'background 0.2s',
                        }} />
                      );
                    })}
                  </div>
                )}

                <button type="submit" disabled={loading || !token}
                  style={{
                    padding: '14px', marginTop: 4,
                    background: loading ? '#9CA3AF' : T.btnBg,
                    color: '#fff', border: 'none', borderRadius: 10,
                    fontSize: 16, fontWeight: 700,
                    cursor: loading ? 'default' : 'pointer',
                    transition: 'background 0.2s',
                  }}>
                  {loading ? 'Updating…' : 'Set New Password →'}
                </button>

                <p style={{ textAlign: 'center', margin: 0 }}>
                  <a href="/login" style={{ color: '#6B7280', fontSize: 13, textDecoration: 'none' }}>
                    ← Back to login
                  </a>
                </p>
              </form>
            )}
          </>
        )}
      </div>
    </div>
  );
}
